import * as THREE from 'three';
import { lightShare } from '../../sim/light';
import { hash3 } from '../fishSwim';
import type { FogUniforms } from '../skin';
import type { HourSun } from '../sunlight';
import { fogOnly, seenLight } from './floor';

/**
 * The water a habitat scene is seen through (M110, the user's third brief: "attractive, readable underwater
 * atmosphere: clearer shallow water where supported by simulated conditions, blue distance fog, ... appropriate solar
 * lighting, depth-dependent attenuation, reduced prominence of suspended white square particles"). Shared by every
 * habitat: the water all round the camera as a dome of its color, lighter toward the surface and darker below; the fog
 * every fogged surface and sprite fades into, the same color at the dome's horizon so the far floor melts into it;
 * the surface's underside overhead where the scene has one; and the motes drifting through it. Scene units are
 * meters, the surface at y 0, so a point's real depth is its -y.
 *
 * Each figure a rendering choice, said so (JUDGED unless said):
 *
 * - `fogPerKd`: the fog's density, per meter of water looked through, for each m⁻¹ of the water's measured clarity
 *   (its PAR's attenuation, `Environment.kdPar`): at Maui Nui's 0.065 to 0.079 m⁻¹ (`light.ts`) the water has hidden
 *   63% (1 - 1/e, the fog being 1 - exp(-density x distance)) of what lies about 33 to 40 m away, and two thirds at
 *   37 to 44 m (review M116: it read "two thirds" at 33 to 40) (JUDGED, by eye: no measured horizontal visibility
 *   read); murkier water, its fog thicker in proportion.
 * - `deepFog`: how much denser the fog is at the scene's deepest than at the surface, and the depth that is reached
 *   at, m: the water darker and closer about the camera with depth.
 * - `colors`: the water's color near the surface toward the sun (`up`), level (`level`, the fog's), and looking down
 *   (`down`), by day, sRGB; `night` its share of them at night; `green` the coast's water, laid on with the simulated
 *   chlorophyll as the exhibit's is (`WATER_STATE` there): none at `chlClear` mg m⁻³, `mostGreen` of the way at
 *   `chlGreen`.
 * - `motes`: specks of marine snow and plankton, `px` across, dim, round (the brief: not "white square particles"),
 *   in a box about the camera `boxM` on a side, `perM3` a cubic meter at the simulated zooplankton's reference
 *   `zooRef` mmol N m⁻³ and in proportion, at most `most`.
 */
const HABITAT_WATER = {
  fogPerKd: 0.38,
  deepFog: { times: 2.2, atM: 600 },
  colors: { up: 0x86e4f2, level: 0x2399c4, down: 0x0b4a78, night: 0.12, green: 0x2f7f6a, chlClear: 0.1, chlGreen: 3, mostGreen: 0.35 },
  motes: { px: 2, opacity: 0.28, boxM: 24, perM3: 0.04, zooRef: 0.4, most: 900 },
} as const;

/** A color's ramp by how far toward the deep a direction or a depth lies, 0 up to 1 down. */
function waterColor(up: THREE.Color, level: THREE.Color, down: THREE.Color, t: number, out: THREE.Color): THREE.Color {
  return t < 0.5 ? out.copy(up).lerp(level, t / 0.5) : out.copy(level).lerp(down, (t - 0.5) / 0.5);
}

/** A soft round dot, 16 by 16, white, its alpha falling to its edge: a mote's picture. */
function dotTexture(): THREE.DataTexture {
  const n = 16;
  const rgba = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const r = Math.hypot(x + 0.5 - n / 2, y + 0.5 - n / 2) / (n / 2);
      rgba.set([255, 255, 255, Math.round(255 * Math.max(0, 1 - r) ** 1.5)], (y * n + x) * 4);
    }
  }
  const t = new THREE.DataTexture(rgba, n, n);
  t.needsUpdate = true;
  return t;
}

export class HabitatWater {
  readonly group = new THREE.Group();
  /** The light seen down here now, 0 to 1, by the light at the camera's depth (`setLight`), for the scene's animals. */
  seen = 1;
  private readonly dome: THREE.Mesh;
  private readonly domeColors: THREE.BufferAttribute;
  private readonly domeUp: Float32Array;
  private readonly underside: THREE.Mesh | null;
  private readonly motes: THREE.Points | null;
  private readonly fogged: Partial<FogUniforms>[] = [];
  private greenness = 0;
  private density = HABITAT_WATER.fogPerKd * 0.07;
  private zoo: number = HABITAT_WATER.motes.zooRef;
  private moteSeconds = 0;
  /** How far toward the deep's own color the water about the camera is, 0 at the surface to 1 at `deepFog.atM`. */
  private deep = 0;
  /** Each mote's place in its box, m, by its fixed hash. */
  private readonly moteBase: number[] = [];
  private readonly fogColor = new THREE.Color(HABITAT_WATER.colors.level);
  private readonly fogDeep = new THREE.Color(HABITAT_WATER.colors.down);

  /**
   * @param box the water's box, world space: the fog's slab, where the eye's line runs through water
   * @param surface whether the surface's underside is drawn overhead (the shallow scenes'); the deep ones look up into dark
   * @param seed the scene's place's seed, for where its motes stand
   * @param fogPower the veil's curve (`FogUniforms.uFogPower`): 1 the water's own, 2 lighter near and heavier far (M169, Maui Nui Reef's)
   * @param motes whether its motes are drawn: not where the scene draws its water's particles itself (M173, the Reef's `ReefParticles`)
   */
  constructor(private readonly box: THREE.Box3, surface: boolean, private readonly seed: number, private readonly fogPower = 1, motes = true) {
    // Fine enough that its colors, laid at its corners, ramp without a band (M117, the brief's "abrupt horizontal color
    // transition in the water": at 16 rows the ramp's kinks fell 11° apart).
    const sphere = new THREE.SphereGeometry(1, 64, 48);
    const position = sphere.getAttribute('position');
    this.domeUp = new Float32Array(position.count);
    for (let i = 0; i < position.count; i++) this.domeUp[i] = position.getY(i);
    this.domeColors = new THREE.BufferAttribute(new Float32Array(position.count * 3), 3);
    sphere.setAttribute('color', this.domeColors);
    this.dome = new THREE.Mesh(sphere, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide, depthWrite: false, depthTest: false, fog: false }));
    this.dome.renderOrder = -10;
    this.dome.frustumCulled = false;
    this.dome.raycast = () => undefined;
    this.group.add(this.dome);
    if (surface) {
      const size = Math.max(box.max.x - box.min.x, box.max.z - box.min.z) * 3;
      // Fogged as all else is, so far off it melts into the water's own color, as the surface does seen from below.
      const material = new THREE.MeshBasicMaterial({ color: HABITAT_WATER.colors.up, side: THREE.BackSide, depthWrite: false });
      this.underside = new THREE.Mesh(new THREE.PlaneGeometry(size, size), material);
      this.fog(fogOnly(material));
      this.underside.rotation.x = -Math.PI / 2;
      this.underside.position.set((box.min.x + box.max.x) / 2, box.max.y, (box.min.z + box.max.z) / 2);
      this.underside.renderOrder = -5;
      this.underside.raycast = () => undefined;
      this.group.add(this.underside);
    } else this.underside = null;
    if (motes) {
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(HABITAT_WATER.motes.most * 3), 3));
      geometry.setDrawRange(0, 0);
      this.motes = new THREE.Points(geometry, new THREE.PointsMaterial({ color: 0xdff4ff, size: HABITAT_WATER.motes.px, sizeAttenuation: false, map: dotTexture(), transparent: true, opacity: HABITAT_WATER.motes.opacity, depthWrite: false }));
      this.motes.frustumCulled = false;
      this.motes.renderOrder = 6;
      this.motes.raycast = () => undefined;
      this.group.add(this.motes);
    } else this.motes = null;
    this.paint(1, 1);
  }

  /** Fogs a surface or a batch of sprites with this water (its uniforms, `attachSkin`'s or `FishBatch.fogUniforms`). */
  fog(skin: Partial<FogUniforms>): void {
    if (!skin.uFogBoxMin || !skin.uFogBoxMax || !skin.uFogDensity || !skin.uFogColor || !skin.uFogDeep || !skin.uFogDeepAt || !skin.uFogDeepDensity) throw new Error('a fogged surface without its fog');
    this.fogged.push(skin);
    skin.uFogBoxMin.value.copy(this.box.min);
    skin.uFogBoxMax.value.copy(this.box.max);
    skin.uFogDeepAt.value.set(0, -HABITAT_WATER.deepFog.atM);
    skin.uFogDeepDensity.value = HABITAT_WATER.deepFog.times;
    if (skin.uFogPower) skin.uFogPower.value = this.fogPower;
    this.refog();
  }

  private refog(): void {
    for (const skin of this.fogged) {
      if (skin.uFogDensity) skin.uFogDensity.value = this.density;
      skin.uFogColor?.value.copy(this.fogColor);
      skin.uFogDeep?.value.copy(this.fogDeep);
    }
  }

  /**
   * The water's measured state on the day (the shared simulation's, `HabitatEnvironment`): its fog by its clarity, its
   * color greener with its chlorophyll, its motes by its zooplankton.
   */
  setState(kdPar: number, chlorophyllMgM3: number, zooMmolNPerM3: number): void {
    const c = HABITAT_WATER.colors;
    this.density = HABITAT_WATER.fogPerKd * Math.max(kdPar, 0.01);
    const span = Math.log10(c.chlGreen / c.chlClear);
    this.greenness = c.mostGreen * Math.min(1, Math.max(0, Math.log10(Math.max(chlorophyllMgM3, 1e-6) / c.chlClear) / span));
    this.zoo = Math.max(0, zooMmolNPerM3);
    this.layMotes();
  }

  /** The fog's density per meter and its color, as last set, for the tests. */
  state(): { fogPerM: number; fogColor: string; greenness: number; motes: number } {
    return { fogPerM: this.density, fogColor: this.fogColor.getHexString(), greenness: this.greenness, motes: this.motes?.geometry.drawRange.count ?? 0 };
  }

  /**
   * The light at an hour, at a depth (m, the camera's): the dome and the fog by the share of the surface's light that
   * reaches it then, on the eye's log scale floored so the deep and the night are seen at all (`seenLight`, the whole
   * tank's), dimmed toward `night` by the sky's light, so the twilight's water is dark at noon; and the animals' light
   * (`seen`), that against a full sun's at that depth, so a fish keeps its colors by day at any depth, as the whole
   * tank's do (`ExhibitView.setLight`).
   */
  setLight(sun: HourSun, depthM: number): void {
    const moon = sun.moon?.level ?? 0;
    const strength = Math.max(Math.min(1, sun.level), 1e-3 * moon);
    const share = lightShare(sun.kdPar, Math.max(0, depthM));
    this.seen = seenLight(strength * share) / seenLight(share);
    this.deep = Math.min(1, Math.max(0, depthM / HABITAT_WATER.deepFog.atM));
    this.paint(seenLight(strength * share), HABITAT_WATER.colors.night + (1 - HABITAT_WATER.colors.night) * sun.sky);
  }

  /** The dome's and the fog's colors at a light: lighter up, its level the fog's, darker down; greener with the chlorophyll. */
  private paint(light: number, sky: number): void {
    const c = HABITAT_WATER.colors;
    const green = new THREE.Color(c.green);
    const tone = (hex: number): THREE.Color => new THREE.Color(hex).lerp(green, this.greenness).multiplyScalar(light * sky);
    // Deep down, the water all round the fog's deep color, as the fog there is, so the far floor melts into it.
    const deep = tone(c.down).multiplyScalar(0.35);
    const [up, level, down] = [tone(c.up), tone(c.level), tone(c.down)].map((x) => x.lerp(deep, this.deep)) as [THREE.Color, THREE.Color, THREE.Color];
    const out = new THREE.Color();
    // By the direction's angle above or below level, evenly (M117: by its sine, the ramp from up to level had crowded into
    // the 20° over the horizon, a band).
    for (let i = 0; i < this.domeUp.length; i++) {
      waterColor(up, level, down, 0.5 - Math.asin(Math.max(-1, Math.min(1, this.domeUp[i] as number))) / Math.PI, out);
      this.domeColors.setXYZ(i, out.r, out.g, out.b);
    }
    this.domeColors.needsUpdate = true;
    this.fogColor.copy(level);
    this.fogDeep.copy(deep);
    if (this.underside) (this.underside.material as THREE.MeshBasicMaterial).color.copy(up);
    if (this.motes) (this.motes.material as THREE.PointsMaterial).opacity = HABITAT_WATER.motes.opacity * light;
    this.refog();
  }

  /** The motes for the water's zooplankton: by a fixed hash in a box `boxM` on a side, the same ones standing as the count moves. */
  private layMotes(): void {
    if (!this.motes) return;
    const m = HABITAT_WATER.motes;
    const n = Math.min(m.most, Math.round(m.perM3 * (this.zoo / m.zooRef) * m.boxM ** 3));
    for (let i = this.moteBase.length / 3; i < n; i++) this.moteBase.push(hash3(this.seed, i, 1) * m.boxM, hash3(this.seed, i, 2) * m.boxM, hash3(this.seed, i, 3) * m.boxM);
    this.motes.geometry.setDrawRange(0, n);
  }

  /**
   * The dome kept about the camera, out at `radius`; each mote at its own place in the water, drifting slowly by
   * `seconds` of the player's time (none when `calm`), and the one of its copies a box apart that lies in the box about
   * the camera, so the water near it is full of them wherever it goes and each stays put as the camera moves past.
   */
  follow(camera: THREE.Camera, radius: number, seconds: number, calm = false): void {
    this.dome.position.copy(camera.position);
    this.dome.scale.setScalar(radius);
    if (!this.motes) return;
    if (!calm) this.moteSeconds = (this.moteSeconds + seconds) % 3600;
    const box = HABITAT_WATER.motes.boxM;
    const wrap = (v: number): number => ((v % box) + box) % box;
    const [dx, dy, dz] = [0.05 * this.moteSeconds, 0.012 * this.moteSeconds, 0.03 * this.moteSeconds];
    const { x, y, z } = camera.position;
    const position = this.motes.geometry.getAttribute('position') as THREE.BufferAttribute;
    const base = this.moteBase;
    const n = this.motes.geometry.drawRange.count;
    const half = box / 2;
    // Each kept under the surface.
    for (let i = 0; i < n; i++) {
      position.setXYZ(i,
        x - half + wrap((base[i * 3] as number) + dx - x + half),
        Math.min(this.box.max.y - 0.05, y - half + wrap((base[i * 3 + 1] as number) + dy - y + half)),
        z - half + wrap((base[i * 3 + 2] as number) + dz - z + half));
    }
    position.needsUpdate = true;
  }

  dispose(): void {
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh || o instanceof THREE.Points) {
        o.geometry.dispose();
        const m = o.material as THREE.Material & { map?: THREE.Texture | null };
        m.map?.dispose();
        m.dispose();
      }
    });
  }
}
